import type { LoaderFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { organizationAPI, teamAPI, podcastAPI, billingAPI, APIError } from "../../lib/api";
import type {
  OrganizationResponse,
  OrganizationPermissions,
  MemberResponse,
  TeamResponse,
  PodcastResponse,
  BillingStatus,
} from "../../types/api";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

export interface OrganizationDetailData {
  organization: OrganizationResponse;
  permissions: OrganizationPermissions;
  members: MemberResponse[];
  teams: TeamResponse[];
  podcasts: PodcastResponse[];
}

export interface OrganizationSettingsData {
  organization: OrganizationResponse;
  permissions: OrganizationPermissions;
}

export interface MembersData {
  organization: OrganizationResponse;
  permissions: OrganizationPermissions;
  members: MemberResponse[];
  billing: BillingStatus | null;
}

export async function organizationDetailLoader({
  params,
}: LoaderFunctionArgs): Promise<OrganizationDetailData> {
  const { id } = params;

  if (!id || !isValidUUID(id)) {
    throw redirect("/organizations");
  }

  try {
    const [orgResponse, members, teams, podcasts] = await Promise.all([
      organizationAPI.getById(id),
      organizationAPI.getMembers(id),
      teamAPI.getAll(id),
      podcastAPI.getAll(id).catch(() => [] as PodcastResponse[]),
    ]);

    return {
      organization: orgResponse.organization,
      permissions: orgResponse.permissions,
      members,
      teams,
      podcasts,
    };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect("/organizations");
    }
    throw error;
  }
}

export async function organizationSettingsLoader({
  params,
}: LoaderFunctionArgs): Promise<OrganizationSettingsData> {
  const { id } = params;

  if (!id || !isValidUUID(id)) {
    throw redirect("/organizations");
  }

  try {
    const response = await organizationAPI.getById(id);
    return {
      organization: response.organization,
      permissions: response.permissions,
    };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect("/organizations");
    }
    throw error;
  }
}

export async function inviteMemberLoader({ params }: LoaderFunctionArgs): Promise<MembersData> {
  const { id } = params;

  if (!id || !isValidUUID(id)) {
    throw redirect("/organizations");
  }

  try {
    const [orgResponse, members] = await Promise.all([
      organizationAPI.getById(id),
      organizationAPI.getMembers(id),
    ]);

    let billing: BillingStatus | null = null;
    try {
      const billingResponse = await billingAPI.getStatus(id);
      billing = billingResponse.billing;
    } catch {
      // Billing may not be available (non-owner or stripe not configured)
    }

    return {
      organization: orgResponse.organization,
      permissions: orgResponse.permissions,
      members,
      billing,
    };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect("/organizations");
    }
    throw error;
  }
}
